import { MicVAD } from '@ricky0123/vad-web'
import { eventChannel, END } from 'redux-saga'

const DEFAULT_SAMPLE_RATE = 16000

function float32ToWav(float32Buf, { sampleRate = DEFAULT_SAMPLE_RATE } = {}) {
  const pcm16 = new Int16Array(float32Buf.length)
  for (let i = 0; i < float32Buf.length; i++) {
    const s = Math.max(-1, Math.min(1, float32Buf[i]))
    pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff
  }

  const wavBytes = 44 + pcm16.byteLength
  const buffer = new ArrayBuffer(wavBytes)
  const view = new DataView(buffer)

  const writeStr = (dv, offset, str) => {
    for (let i = 0; i < str.length; i++) dv.setUint8(offset + i, str.charCodeAt(i))
  }

  writeStr(view, 0, 'RIFF')
  view.setUint32(4, wavBytes - 8, true)
  writeStr(view, 8, 'WAVE')
  writeStr(view, 12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, 1, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * 2, true)
  view.setUint16(32, 2, true)
  view.setUint16(34, 16, true)
  writeStr(view, 36, 'data')
  view.setUint32(40, pcm16.byteLength, true)

  new Int16Array(buffer, 44).set(pcm16)

  return new Blob([buffer], { type: 'audio/wav' })
}

function concatFloat32Chunks(chunks) {
  let totalLength = 0
  for (const c of chunks) totalLength += c.length
  const out = new Float32Array(totalLength)
  let offset = 0
  for (const c of chunks) {
    out.set(c, offset)
    offset += c.length
  }
  return out
}

/**
 * Creates a redux-saga eventChannel emitting:
 *  - { type: 'speechStart' }
 *  - { type: 'frame', frame }
 *  - { type: 'speechEnd', floatAudio }
 *  - { type: 'error', error }
 */
export function createVadChannel({ debug = false } = {}) {
  let vad = null

  return eventChannel((emit) => {
    let destroyed = false
    let lastVadEventAt = Date.now()

    const touch = (label) => {
        lastVadEventAt = Date.now()
        if (debug) {
          console.log('[STT][VAD][RAW]', label, new Date().toISOString())
        }
      }

    ;(async () => {
      try {
        vad = await MicVAD.new({
          onSpeechStart: () => {
            touch('speechStart')
            emit({ type: 'speechStart' })
          },
          onFrameProcessed: (probabilities, frame) => {
            if (Math.random() < 0.05) {
              touch('frame')
            }
            emit({ type: 'frame', frame, probabilities })
          },
          onSpeechEnd: (floatAudio) => {
            touch('speechEnd')
            emit({ type: 'speechEnd', floatAudio })
          },
        })

        if (destroyed) {
          await vad.destroy()
          vad = null
          return
        }

        vad.start()
      } catch (error) {
        emit({ type: 'error', error })
        emit(END)
      }
    })()

    const intervalId = setInterval(() => {
      const age = Date.now() - lastVadEventAt
      if (age > 15000) {
        console.warn('[STT][VAD][RAW] 2841423 no events for', age, 'ms')
      }
    }, 5000)

    return async () => {
      destroyed = true
      try {
        clearInterval(intervalId)
      } catch (e) {}

      try {
        if (vad) {
          await vad.destroy()
          vad = null
        }
      } catch (e) {
        console.error('gizim 238723', e)
      }
    }
  })

}

export function makeTranscriptionForm(floatAudio, { sampleRate = DEFAULT_SAMPLE_RATE } = {}) {
  const wavBlob = float32ToWav(floatAudio, { sampleRate })
  const form = new FormData()
  form.append('wavBlob', wavBlob, 'wavBlob.wav')
  return form
}

export function normalizeTranscript(text) {
  return (text || '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N} ]+/gu, '')
    .trim()
}

export function shouldStopMicByVoiceCommand(transcriptClean) {
  return [
    'עצור תימלול',
    'עצור דיבור',
    'לא להאזין',
    'לא להקליט',
    'לא להקליט דיבור',
    'לא להקליט שיחה',
    'לא להקשיב',
    'לא להקשיב לדיבור',
    'לא להקשיב לשיחה',
    'לא להקשיב למיקרופון',
    'כבה מיקרופון',
    'כבה את המיקרופון',
    'לא לתמלל',
    'לעצור תימלול',
    'לעצור תמלול',
  ].includes(transcriptClean)
}

export const __private = { concatFloat32Chunks }
